import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import "./CanvasHeightEditor.css";
import {
  IMAGE_PRESETS_CHANGED_EVENT,
  loadImagePresets,
  persistImagePresets,
} from "../../utils/imagePresetsStorage";

const TYPING_INPUT_TYPES = new Set([
  "insertText",
  "deleteContentBackward",
  "deleteContentForward",
  "insertCompositionText",
  "insertFromPaste",
  "insertFromDrop",
  "deleteByCut",
]);

const shouldDeferInputUpdate = (inputType) =>
  inputType && TYPING_INPUT_TYPES.has(inputType);

const MIN_CANVAS_HEIGHT_PERCENT = 5;

/** Same clamp as MyCanvas `applyCanvasHeight` / min-height effect (content-based floor above 5%). */
const computeEffectiveMinCanvasHeightPercent = (getMinFn) => {
  if (typeof getMinFn !== "function") {
    return MIN_CANVAS_HEIGHT_PERCENT;
  }
  try {
    const minPercent = getMinFn();
    const raw =
      typeof minPercent === "number" && !Number.isNaN(minPercent)
        ? minPercent
        : 0;
    return Math.min(
      100,
      Math.max(MIN_CANVAS_HEIGHT_PERCENT, Math.ceil(raw)),
    );
  } catch {
    return MIN_CANVAS_HEIGHT_PERCENT;
  }
};

// Must match RectangleEditor / CircleEditor / UnifiedLineArrowEditor / ImageEditor
const RECTANGLE_DEFAULT_STYLE_PRESET_KEY = "rectangleDefaultStylePreset";
const CIRCLE_DEFAULT_STYLE_PRESET_KEY = "circleDefaultStylePreset";
const UNIFIED_LINE_ARROW_DEFAULT_STYLE_PRESET_KEY =
  "unifiedLineArrowDefaultStylePreset";
const IMAGE_DEFAULT_STYLE_PRESET_KEY = "imageDefaultStylePreset";
const ONE_SHOT_PRESET_KEY_SUFFIX = "OneShot";

const createDefaultRectanglePreset = () => ({
  id: "rectangle-default-preset",
  name: "Default Rectangle",
  fill: "#FFFFFF",
  fillOpacity: 1,
  stroke: "#FF0000",
  strokeOpacity: 1,
  strokeWidth: 2,
  fontSize: 15,
  textColor: "#000000",
  fontStyle: "normal",
  textDecoration: "none",
  align: "center",
  verticalAlign: "middle",
  text: "",
  width: 50,
  height: 50,
  rotation: 0,
  maintainAspectRatio: false,
});

const createDefaultCirclePreset = () => ({
  id: "circle-default-preset",
  name: "Default Circle",
  fill: "#FFFFFF",
  fillOpacity: 1,
  stroke: "#FF0000",
  strokeOpacity: 1,
  strokeWidth: 2,
  fontSize: 15,
  textColor: "#000000",
  fontStyle: "normal",
  textDecoration: "none",
  align: "center",
  verticalAlign: "middle",
  text: "",
  rotation: 0,
  radius: 25,
  radiusX: 25,
  radiusY: 25,
  maintainAspectRatio: true,
});

const createDefaultLineArrowPreset = () => ({
  id: "unified-line-arrow-default-preset",
  name: "Default Line & Arrow",
  type: "arrow",
  stroke: "#FF0000",
  strokeColor: "#FF0000",
  strokeWidth: 2,
  isDashed: false,
  hasStartArrow: false,
  hasEndArrow: true,
  pointerAtBeginning: false,
  pointerAtEnd: true,
  pointerAtEnding: true,
  points: [0, 0, 100, 0],
  width: 100,
  height: 0,
  rotation: 0,
});

const parseStorageArray = (key) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

/** Clear default-for-new-draws storage when the deleted preset was the default. */
const clearDefaultStylePresetIfDeleted = (deletedPresetId, presetJsonKey) => {
  const idKey = `${presetJsonKey}Id`;
  const storedId = localStorage.getItem(idKey);
  if (storedId != null && String(storedId) === String(deletedPresetId)) {
    localStorage.removeItem(presetJsonKey);
    localStorage.removeItem(idKey);
  }
};

const CanvasHeightEditor = ({
  canvasHeight,
  setCanvasHeight,
  applyCanvasHeight,
  onAutoSizeCanvas,
  setImages,
  images,
  setRectangles,
  rectangles,
  setCircles,
  circles,
  setLines,
  lines,
  addImage,
  setCanvasResizeTrigger,
  setSelectedObject,
  setSelectedImage,
  setSelectedObjects,
  setSelectedText,
  setSelectedTextId,
  setSelectedArrow,
  setSelectedLine,
  setSelectedDoubleSidedArrow,
  setAction,
  ACTIONS,
  setLockedAction,
  setShowTextBox,
  setPendingImageData,
  transformerRef,
  saveToHistory,
  onHeightChangeCommit, // Callback to expose commit function to parent
  onUserChange, // Optional: mark that user made changes (e.g. for unsaved blocker)
  editorWidth = 320,
  getMinCanvasHeightPercent,
}) => {
  const effectiveMinPercent = computeEffectiveMinCanvasHeightPercent(
    getMinCanvasHeightPercent,
  );
  const parsedFromProp = parseInt(canvasHeight, 10);
  const numericHeight = Number.isNaN(parsedFromProp)
    ? effectiveMinPercent
    : Math.max(effectiveMinPercent, parsedFromProp);
  const [inputHeight, setInputHeight] = useState(numericHeight);
  const [isEditing, setIsEditing] = useState(false);

  // Track pending height changes for grouping/debouncing
  const pendingHeightChangeRef = useRef(null);
  const heightChangeTimeoutRef = useRef(null);
  const initialHeightRef = useRef(numericHeight);
  const isChangingHeightRef = useRef(false);

  // Sync input with canvasHeight prop when not actively editing (respect dynamic min, e.g. ~30%)
  React.useEffect(() => {
    if (!isEditing) {
      const parsed = parseInt(canvasHeight, 10);
      if (Number.isNaN(parsed)) {
        setInputHeight(effectiveMinPercent);
      } else {
        setInputHeight(Math.max(effectiveMinPercent, parsed));
      }
    }
  }, [canvasHeight, isEditing, effectiveMinPercent]);

  const handleChange = (e) => {
    const { value } = e.target;
    const inputType = e.nativeEvent?.inputType;
    setInputHeight(value);
    setIsEditing(true);
    if (value === "" || shouldDeferInputUpdate(inputType)) {
      return;
    }
    commitHeightValue(value);
  };

  const handleEnterKey = (event, commitFn) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitFn();
    }
  };

  // Commit pending height changes to history
  const commitPendingHeightChange = useCallback(
    (stateOverrides = null) => {
      if (pendingHeightChangeRef.current !== null) {
        const pendingHeight = pendingHeightChangeRef.current;
        const initialHeight = initialHeightRef.current;

        // Clear flags BEFORE calling saveToHistory to prevent hasPendingHeightChange from returning true
        isChangingHeightRef.current = false;

        // Only save if height actually changed from initial
        if (
          pendingHeight !== initialHeight &&
          typeof saveToHistory === "function"
        ) {
          // Temporarily clear pendingHeightChangeRef so hasPendingHeightChange returns false
          pendingHeightChangeRef.current = null;

          // Save synchronously to ensure it happens BEFORE the next operation
          // This ensures correct history order: height change first, then other operation
          // Pass state overrides if provided (to use state from before the current operation)
          const heightOverride = { currentCanvasHeightOverride: pendingHeight };
          if (stateOverrides) {
            saveToHistory(
              "canvas_height_change",
              stateOverrides.currentImagesOverride || null,
              stateOverrides.currentArrowsOverride || null,
              stateOverrides.currentDoubleSidedArrowsOverride || null,
              stateOverrides.currentLinesOverride || null,
              stateOverrides.currentTextsOverride || null,
              stateOverrides.currentSelectedTextOverride || null,
              stateOverrides.currentSelectedTextIdOverride || null,
              stateOverrides.currentRectanglesOverride || null,
              stateOverrides.currentOffCanvasRectanglesOverride || null,
              stateOverrides.currentCirclesOverride || null,
              stateOverrides.currentOffCanvasCirclesOverride || null,
              stateOverrides.currentOffCanvasArrowsOverride || null,
              stateOverrides.currentOffCanvasDoubleSidedArrowsOverride || null,
              stateOverrides.currentOffCanvasLinesOverride || null,
              stateOverrides.currentOffCanvasTextsOverride || null,
              stateOverrides.currentOffCanvasImagesOverride || null,
              null,
              null,
              heightOverride
            );
          } else {
            saveToHistory(
              "canvas_height_change",
              null, null, null, null, null, null, null, null, null, null,
              null, null, null, null, null, null, null, null,
              heightOverride
            );
          }
        } else {
          // No change, just clear the ref
          pendingHeightChangeRef.current = null;
        }

        // Update initial height to the committed value
        initialHeightRef.current = pendingHeight;
      }

      // Clear any pending timeout
      if (heightChangeTimeoutRef.current) {
        clearTimeout(heightChangeTimeoutRef.current);
        heightChangeTimeoutRef.current = null;
      }
    },
    [saveToHistory]
  );

  const commitHeightValue = (rawValue, isArrowKeyChange = false) => {
    const previousPercent = parseInt(canvasHeight, 10);
    let num = parseInt(rawValue, 10);
    if (rawValue === "" || Number.isNaN(num)) {
      num = parseInt(canvasHeight, 10);
      if (Number.isNaN(num)) num = effectiveMinPercent;
      else num = Math.max(effectiveMinPercent, num);
    }
    if (num < effectiveMinPercent) num = effectiveMinPercent;
    if (num > 100) num = 100;

    setInputHeight(num);
    setIsEditing(false);

    // For arrow key changes, capture initial height BEFORE updating
    // This ensures we have the correct starting point for grouping
    if (isArrowKeyChange && pendingHeightChangeRef.current === null) {
      const p = parseInt(canvasHeight, 10);
      initialHeightRef.current = Number.isNaN(p)
        ? effectiveMinPercent
        : Math.max(effectiveMinPercent, p);
    }

    // Update both local state and canvas height using constraint-aware setter when available
    let appliedPercent;
    if (typeof applyCanvasHeight === "function") {
      appliedPercent = applyCanvasHeight(num);
    } else {
      if (typeof setCanvasHeight === "function") {
        setCanvasHeight(String(num));
      }
      appliedPercent = num;
    }

    // If the height actually changed (including via arrow stepper), mark it as a user change.
    // This ensures the unsaved-changes blocker triggers for numeric input up/down changes.
    if (
      typeof onUserChange === "function" &&
      typeof appliedPercent === "number" &&
      !Number.isNaN(appliedPercent) &&
      appliedPercent !== previousPercent
    ) {
      onUserChange();
    }

    setInputHeight(
      typeof appliedPercent === "number" && !Number.isNaN(appliedPercent)
        ? appliedPercent
        : num
    );

    // For arrow key changes, only track the change - don't save to history yet
    // History will be saved when another operation occurs (handled in historyUtils.js)
    // or when user blurs/presses Enter
    if (isArrowKeyChange) {
      isChangingHeightRef.current = true;

      // Update pending height to the latest value
      pendingHeightChangeRef.current = appliedPercent;

      // Don't save to history - wait for another operation or blur/enter
      // The commitPendingHeightChange will be called from historyUtils.js when another operation happens
    } else {
      // For non-arrow changes (blur, enter, etc.), just track the change
      // Don't save to history - wait for another operation to trigger the save
      // This groups all height changes (arrow keys + blur/enter) into one history entry
      if (pendingHeightChangeRef.current === null) {
        // Initialize initial height if this is the first change
        const p = parseInt(canvasHeight, 10);
        initialHeightRef.current = Number.isNaN(p)
          ? effectiveMinPercent
          : Math.max(effectiveMinPercent, p);
      }

      // Update pending height to the latest value
      pendingHeightChangeRef.current = appliedPercent;
      isChangingHeightRef.current = true;

      // Don't save to history - wait for another operation
      // The commitPendingHeightChange will be called from historyUtils.js when another operation happens
    }

    // Trigger canvas resize to update TextElement visibility with a small delay
    // to ensure canvas height is updated first
    if (setCanvasResizeTrigger) {
      setTimeout(() => {
        setCanvasResizeTrigger((prev) => prev + 1);
      }, 10);
    }
  };

  // Sync initial height when canvasHeight prop changes externally (not from our edits)
  useEffect(() => {
    if (!isChangingHeightRef.current && !isEditing) {
      const p = parseInt(canvasHeight, 10);
      initialHeightRef.current = Number.isNaN(p)
        ? effectiveMinPercent
        : Math.max(effectiveMinPercent, p);
    }
  }, [canvasHeight, isEditing, effectiveMinPercent]);

  // Expose commit function and check function to parent
  // Use a ref to store the check function so it always reads current ref values
  const hasPendingChangeRef = useRef(() => false);
  hasPendingChangeRef.current = () => {
    return (
      pendingHeightChangeRef.current !== null && isChangingHeightRef.current
    );
  };

  useEffect(() => {
    if (typeof onHeightChangeCommit === "function") {
      onHeightChangeCommit({
        commit: commitPendingHeightChange,
        hasPendingChange: () => hasPendingChangeRef.current(),
      });
    }
  }, [onHeightChangeCommit, commitPendingHeightChange]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (heightChangeTimeoutRef.current) {
        clearTimeout(heightChangeTimeoutRef.current);
      }
    };
  }, []);

  const [imagePresets, setImagePresets] = useState([]);
  const [lineArrowPresets, setLineArrowPresets] = useState(() => {
    const stored = parseStorageArray("sharedPresets");
    return stored.length > 0 ? stored : [createDefaultLineArrowPreset()];
  });
  const [circlePresets, setCirclePresets] = useState(() => {
    const stored = parseStorageArray("circleStylePresets");
    return stored.length > 0 ? stored : [createDefaultCirclePreset()];
  });
  const [rectanglePresets, setRectanglePresets] = useState(() => {
    const stored = parseStorageArray("rectangleStylePresets");
    return stored.length > 0 ? stored : [createDefaultRectanglePreset()];
  });
  const [isCanvasHeightExpanded, setIsCanvasHeightExpanded] = useState(true);
  const [isImagePresetsExpanded, setIsImagePresetsExpanded] = useState(true);
  const [editingImagePreset, setEditingImagePreset] = useState(null);
  const saveImagePresetsToStorage = (presets) => {
    persistImagePresets(presets);
  };
  const saveLineArrowPresetsToStorage = (presets) => {
    localStorage.setItem("sharedPresets", JSON.stringify(presets));
  };
  const saveCirclePresetsToStorage = (presets) => {
    localStorage.setItem("circleStylePresets", JSON.stringify(presets));
  };
  const saveRectanglePresetsToStorage = (presets) => {
    localStorage.setItem("rectangleStylePresets", JSON.stringify(presets));
  };

  useEffect(() => {
    const ensureShapePresetDefaults = () => {
      const storedLineArrow = parseStorageArray("sharedPresets");
      if (storedLineArrow.length === 0) {
        const fallback = [createDefaultLineArrowPreset()];
        saveLineArrowPresetsToStorage(fallback);
      }

      const storedCircle = parseStorageArray("circleStylePresets");
      if (storedCircle.length === 0) {
        const fallback = [createDefaultCirclePreset()];
        saveCirclePresetsToStorage(fallback);
      }

      const storedRectangle = parseStorageArray("rectangleStylePresets");
      if (storedRectangle.length === 0) {
        const fallback = [createDefaultRectanglePreset()];
        saveRectanglePresetsToStorage(fallback);
      }
    };

    const syncAllPresetsFromStorage = () => {
      loadImagePresets().then(setImagePresets);
      const lineArrow = parseStorageArray("sharedPresets");
      const circles = parseStorageArray("circleStylePresets");
      const rectangles = parseStorageArray("rectangleStylePresets");

      setLineArrowPresets(
        lineArrow.length > 0 ? lineArrow : [createDefaultLineArrowPreset()]
      );
      setCirclePresets(
        circles.length > 0 ? circles : [createDefaultCirclePreset()]
      );
      setRectanglePresets(
        rectangles.length > 0 ? rectangles : [createDefaultRectanglePreset()]
      );
    };

    ensureShapePresetDefaults();
    syncAllPresetsFromStorage();

    const handleStorageUpdate = () => syncAllPresetsFromStorage();
    window.addEventListener("storage", handleStorageUpdate);
    window.addEventListener("focus", handleStorageUpdate);
    window.addEventListener(IMAGE_PRESETS_CHANGED_EVENT, handleStorageUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageUpdate);
      window.removeEventListener("focus", handleStorageUpdate);
      window.removeEventListener(
        IMAGE_PRESETS_CHANGED_EVENT,
        handleStorageUpdate,
      );
    };
  }, []);

  // Inside CanvasHeightEditor.jsx
  // Inside CanvasHeightEditor.jsx

  const applyImagePreset = (preset) => {
    const src = preset?.src;

    if (!src) {
      return;
    }

    // Load the image and apply all preset properties except the name
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = src;

    img.onload = () => {
      // CanvasHeightEditor preset applies to the next creation only.
      // Do not overwrite persistent "default style preset" from element editors.
      localStorage.setItem(
        `${IMAGE_DEFAULT_STYLE_PRESET_KEY}${ONE_SHOT_PRESET_KEY_SUFFIX}`,
        JSON.stringify(preset),
      );

      if (typeof setPendingImageData === "function") {
        setPendingImageData({
          imageSrc: src,
          originalBlob: null,
          naturalWidth: img.naturalWidth,
          naturalHeight: img.naturalHeight,
          loadedImage: img,
        });
      }

      if (typeof setLockedAction === "function") setLockedAction(null);
      if (typeof setAction === "function" && ACTIONS?.IMAGE) {
        setAction(ACTIONS.IMAGE);
      }
      if (typeof setShowTextBox === "function") setShowTextBox(false);
      if (typeof setSelectedObject === "function") setSelectedObject(null);
      if (typeof setSelectedObjects === "function") setSelectedObjects([]);
      if (typeof setSelectedImage === "function") setSelectedImage(null);
      if (typeof setSelectedText === "function") setSelectedText(null);
      if (typeof setSelectedTextId === "function") setSelectedTextId(null);
      if (typeof setSelectedArrow === "function") setSelectedArrow(null);
      if (typeof setSelectedLine === "function") setSelectedLine(null);
      if (typeof setSelectedDoubleSidedArrow === "function") {
        setSelectedDoubleSidedArrow(null);
      }
      if (transformerRef && transformerRef.current) {
        transformerRef.current.nodes([]);
      }
    };

    img.onerror = () => {
      alert("Unable to load image preset for drawing.");
    };
  };

  const applyShapePreset = (preset) => {
    let presetJsonKey = null;
    let targetAction = null;

    if (preset.presetCategory === "rectangle") {
      presetJsonKey = RECTANGLE_DEFAULT_STYLE_PRESET_KEY;
      targetAction = ACTIONS?.RECTANGLE;
    } else if (preset.presetCategory === "circle") {
      presetJsonKey = CIRCLE_DEFAULT_STYLE_PRESET_KEY;
      targetAction = ACTIONS?.CIRCLE;
    } else if (preset.presetCategory === "line_arrow") {
      presetJsonKey = UNIFIED_LINE_ARROW_DEFAULT_STYLE_PRESET_KEY;
      targetAction = ACTIONS?.LINE;
    }

    if (!presetJsonKey || !targetAction || typeof setAction !== "function") {
      return;
    }

    // CanvasHeightEditor preset applies to the next creation only.
    // Do not overwrite persistent "default style preset" from element editors.
    localStorage.setItem(
      `${presetJsonKey}${ONE_SHOT_PRESET_KEY_SUFFIX}`,
      JSON.stringify(preset),
    );

    // Presets should place one element without locking toolbar tool.
    if (typeof setLockedAction === "function") setLockedAction(null);
    setAction(targetAction);
    if (typeof setShowTextBox === "function") setShowTextBox(false);
    if (typeof setSelectedObject === "function") setSelectedObject(null);
    if (typeof setSelectedObjects === "function") setSelectedObjects([]);
    if (typeof setSelectedText === "function") setSelectedText(null);
    if (typeof setSelectedTextId === "function") setSelectedTextId(null);
  };

  const allPresets = [
    ...imagePresets.map((preset) => ({ ...preset, presetCategory: "image" })),
    ...lineArrowPresets.map((preset) => ({
      ...preset,
      presetCategory: "line_arrow",
    })),
    ...circlePresets.map((preset) => ({ ...preset, presetCategory: "circle" })),
    ...rectanglePresets.map((preset) => ({
      ...preset,
      presetCategory: "rectangle",
    })),
  ];

  const getPresetCategoryLabel = (category) => {
    if (category === "image") return "Image";
    if (category === "line_arrow") return "Line/Arrow";
    if (category === "circle") return "Circle";
    if (category === "rectangle") return "Rectangle";
    return "Style";
  };

  const [editingGenericPreset, setEditingGenericPreset] = useState(null);

  useEffect(() => {
    const onCanvasEscape = () => {
      setEditingImagePreset(null);
      setEditingGenericPreset(null);
      setIsEditing(false);
    };
    window.addEventListener("canvasEscape", onCanvasEscape);
    return () => window.removeEventListener("canvasEscape", onCanvasEscape);
  }, []);
  const isPresetEditingActive =
    Boolean(editingImagePreset) || Boolean(editingGenericPreset);

  const isPresetDeletable = (preset) => {
    if (!preset || preset.presetCategory === "image") return true;
    if (preset.presetCategory === "rectangle") return rectanglePresets.length > 1;
    if (preset.presetCategory === "circle") return circlePresets.length > 1;
    if (preset.presetCategory === "line_arrow") return lineArrowPresets.length > 1;
    return true;
  };

  const handleDeletePreset = (preset) => {
    if (!isPresetDeletable(preset)) return;

    if (preset.presetCategory === "image") {
      const filtered = imagePresets.filter((p) => p.id !== preset.id);
      setImagePresets(filtered);
      saveImagePresetsToStorage(filtered);
      clearDefaultStylePresetIfDeleted(preset.id, IMAGE_DEFAULT_STYLE_PRESET_KEY);
      if (editingImagePreset && editingImagePreset.id === preset.id) {
        setEditingImagePreset(null);
      }
      return;
    }

    if (preset.presetCategory === "line_arrow") {
      const filtered = lineArrowPresets.filter((p) => p.id !== preset.id);
      setLineArrowPresets(filtered);
      saveLineArrowPresetsToStorage(filtered);
      clearDefaultStylePresetIfDeleted(
        preset.id,
        UNIFIED_LINE_ARROW_DEFAULT_STYLE_PRESET_KEY,
      );
    } else if (preset.presetCategory === "circle") {
      const filtered = circlePresets.filter((p) => p.id !== preset.id);
      setCirclePresets(filtered);
      saveCirclePresetsToStorage(filtered);
      clearDefaultStylePresetIfDeleted(preset.id, CIRCLE_DEFAULT_STYLE_PRESET_KEY);
    } else if (preset.presetCategory === "rectangle") {
      const filtered = rectanglePresets.filter((p) => p.id !== preset.id);
      setRectanglePresets(filtered);
      saveRectanglePresetsToStorage(filtered);
      clearDefaultStylePresetIfDeleted(
        preset.id,
        RECTANGLE_DEFAULT_STYLE_PRESET_KEY,
      );
    }

    if (
      editingGenericPreset &&
      editingGenericPreset.id === preset.id &&
      editingGenericPreset.presetCategory === preset.presetCategory
    ) {
      setEditingGenericPreset(null);
    }
  };

  const handleSaveGenericPresetName = () => {
    if (!editingGenericPreset) return;
    const trimmedName = editingGenericPreset.name.trim();
    if (!trimmedName) return;

    if (editingGenericPreset.presetCategory === "line_arrow") {
      const isDuplicate = lineArrowPresets.some(
        (p) =>
          p.id !== editingGenericPreset.id &&
          p.name?.trim().toLowerCase() === trimmedName.toLowerCase()
      );
      if (isDuplicate) {
        alert(
          "A preset with this name already exists. Please choose a different name."
        );
        return;
      }
      const updated = lineArrowPresets.map((p) =>
        p.id === editingGenericPreset.id ? { ...p, name: trimmedName } : p
      );
      setLineArrowPresets(updated);
      saveLineArrowPresetsToStorage(updated);
    } else if (editingGenericPreset.presetCategory === "circle") {
      const isDuplicate = circlePresets.some(
        (p) =>
          p.id !== editingGenericPreset.id &&
          p.name?.trim().toLowerCase() === trimmedName.toLowerCase()
      );
      if (isDuplicate) {
        alert(
          "A preset with this name already exists. Please choose a different name."
        );
        return;
      }
      const updated = circlePresets.map((p) =>
        p.id === editingGenericPreset.id ? { ...p, name: trimmedName } : p
      );
      setCirclePresets(updated);
      saveCirclePresetsToStorage(updated);
    } else if (editingGenericPreset.presetCategory === "rectangle") {
      const isDuplicate = rectanglePresets.some(
        (p) =>
          p.id !== editingGenericPreset.id &&
          p.name?.trim().toLowerCase() === trimmedName.toLowerCase()
      );
      if (isDuplicate) {
        alert(
          "A preset with this name already exists. Please choose a different name."
        );
        return;
      }
      const updated = rectanglePresets.map((p) =>
        p.id === editingGenericPreset.id ? { ...p, name: trimmedName } : p
      );
      setRectanglePresets(updated);
      saveRectanglePresetsToStorage(updated);
    }

    setEditingGenericPreset(null);
  };

  return (
    <div
      className="canvas-height-editor"
      data-canvas-chrome
      style={{
        width: `${editorWidth}px`,
        minWidth: `${editorWidth}px`,
        maxWidth: `${editorWidth}px`,
      }}
    >
      {/* Canvas Height Section */}
      <div
        style={{
          background: "#f7f7f7",
          borderRadius: "5px",
          padding: "1rem",
          marginBottom: "1.5rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            marginBottom: isCanvasHeightExpanded ? "8px" : 0,
          }}
          onClick={() => setIsCanvasHeightExpanded(!isCanvasHeightExpanded)}
        >
          <h4 style={{ margin: "0", fontWeight: "normal" }}>Canvas Height</h4>
          <svg
            width="14"
            height="8"
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              transform: isCanvasHeightExpanded
                ? "rotate(0deg)"
                : "rotate(180deg)",
              transition: "transform 0.2s",
            }}
          >
            <path
              d="M1.61906 7.75961L0.734375 6.87492L7.36906 0.240234L14.0038 6.87492L13.1191 7.75961L7.36906 2.0168L1.61906 7.75961Z"
              fill="#6C757D"
            />
          </svg>
        </div>

        {isCanvasHeightExpanded && (
          <>
            <div data-block-leave-canvas>
              {/* <label htmlFor="canvas-height-input">Canvas Height (%):</label> */}
              <input
                id="canvas-height-input"
                type="number"
                value={inputHeight}
                min={effectiveMinPercent}
                max={100}
                onChange={handleChange}
                onBlur={(e) => {
                  // Update the height value
                  commitHeightValue(e.target.value, false);
                  // Save to history immediately on blur (similar to circle orb behavior)
                  // Use requestAnimationFrame to ensure state has updated before saving
                  if (typeof saveToHistory === "function") {
                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => {
                        commitPendingHeightChange();
                      });
                    });
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                    let currentValue = parseInt(e.target.value, 10);
                    if (Number.isNaN(currentValue)) {
                      currentValue = parseInt(canvasHeight, 10);
                    }
                    if (Number.isNaN(currentValue)) {
                      currentValue = effectiveMinPercent;
                    }
                    currentValue = Math.max(effectiveMinPercent, currentValue);
                    const newHeight =
                      e.key === "ArrowUp"
                        ? Math.min(currentValue + 1, 100)
                        : Math.max(currentValue - 1, effectiveMinPercent);
                    commitHeightValue(String(newHeight), true); // true = isArrowKeyChange
                  } else {
                    handleEnterKey(e, () => {
                      commitHeightValue(e.target.value, false);
                      // Save to history immediately on Enter (similar to circle orb behavior)
                      // Use requestAnimationFrame to ensure state has updated before saving
                      if (typeof saveToHistory === "function") {
                        requestAnimationFrame(() => {
                          requestAnimationFrame(() => {
                            commitPendingHeightChange();
                          });
                        });
                      }
                    });
                  }
                }}
                className="canvas-height-input"
                style={{
                  width: "100%",
                  padding: "4px 8px",
                  marginTop: "8px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  background: "#fff",
                }}
              />
            </div>
            <button
              type="button"
              onClick={() => {
                if (typeof onAutoSizeCanvas === "function") {
                  onAutoSizeCanvas();
                }
              }}
              style={{
                width: "100%",
                marginTop: "10px",
                padding: "8px 10px",
                borderRadius: "4px",
                border: "1px solid #0d6efd",
                background: "#0d6efd",
                color: "#fff",
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              Auto Size (Fit Content)
            </button>
          </>
        )}
      </div>

      {/* Image Presets Section */}
      <div
        style={{
          background: "#f7f7f7",
          borderRadius: "5px",
          padding: "1rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
            marginBottom: "5px",
          }}
          onClick={() => setIsImagePresetsExpanded(!isImagePresetsExpanded)}
        >
          <h4 style={{ margin: "0", fontWeight: "normal" }}>Styles</h4>
          <svg
            width="14"
            height="8"
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              transform: isImagePresetsExpanded
                ? "rotate(0deg)"
                : "rotate(180deg)",
              transition: "transform 0.2s",
            }}
          >
            <path
              d="M1.61906 7.75961L0.734375 6.87492L7.36906 0.240234L14.0038 6.87492L13.1191 7.75961L7.36906 2.0168L1.61906 7.75961Z"
              fill="#6C757D"
            />
          </svg>
        </div>

        {isImagePresetsExpanded && (
          <>
            <div className="canvas-height-editor-presets-list">
              {allPresets.map((preset) => {
                const isThisPresetRowBeingEdited =
                  (preset.presetCategory === "image" &&
                    editingImagePreset &&
                    editingImagePreset.id === preset.id) ||
                  (editingGenericPreset &&
                    editingGenericPreset.id === preset.id &&
                    editingGenericPreset.presetCategory ===
                      preset.presetCategory);
                const presetNameApplyLocked =
                  isPresetEditingActive && !isThisPresetRowBeingEdited;
                const isDeleteDisabled =
                  isPresetEditingActive || !isPresetDeletable(preset);
                return (
                <div key={`${preset.presetCategory}-${preset.id}`}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px 0",
                      borderBottom: "1px solid #eee",
                      gap: "16px",
                    }}
                  >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (presetNameApplyLocked) return;
                      if (preset.presetCategory === "image") {
                        applyImagePreset(preset);
                      } else {
                        applyShapePreset(preset);
                      }
                    }}
                    disabled={presetNameApplyLocked}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      cursor: presetNameApplyLocked ? "not-allowed" : "pointer",
                      flexGrow: 1,
                      minWidth: 0,
                      background: "none",
                      border: "none",
                      padding: 0,
                      textAlign: "left",
                      font: "inherit",
                      color: presetNameApplyLocked ? "#9ca3af" : "inherit",
                      opacity: presetNameApplyLocked ? 0.55 : 1,
                    }}
                  >
                    <span
                      style={{
                        display: "block",
                        width: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={preset.name}
                    >
                      {preset.name}
                    </span>
                  </button>

                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isPresetEditingActive) return;
                        if (preset.presetCategory === "image") {
                          setEditingImagePreset(preset);
                        } else {
                          setEditingGenericPreset(preset);
                        }
                      }}
                      disabled={isPresetEditingActive}
                      title="Edit"
                      style={{
                        backgroundColor: "#007bff",
                        border: "none",
                        borderRadius: "4px",
                        cursor: isPresetEditingActive ? "not-allowed" : "pointer",
                        padding: "2px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "22px",
                        height: "22px",
                        opacity: isPresetEditingActive ? 0.45 : 1,
                      }}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clipPath="url(#clip0_15909_16836)">
                          <path
                            d="M11.6263 1.45481C11.6964 1.5251 11.7357 1.6203 11.7357 1.71956C11.7357 1.81881 11.6964 1.91401 11.6263 1.98431L10.844 2.76731L9.34404 1.26731L10.1263 0.484307C10.1966 0.414005 10.292 0.374512 10.3914 0.374512C10.4908 0.374512 10.5862 0.414005 10.6565 0.484307L11.6263 1.45406V1.45481ZM10.3138 3.29681L8.81379 1.79681L3.70404 6.90731C3.66276 6.94858 3.63168 6.99891 3.61329 7.05431L3.00954 8.86481C2.99859 8.89781 2.99703 8.93321 3.00505 8.96705C3.01306 9.00089 3.03033 9.03184 3.05492 9.05643C3.07951 9.08102 3.11045 9.09828 3.14429 9.1063C3.17813 9.11431 3.21353 9.11276 3.24654 9.10181L5.05704 8.49806C5.11236 8.47988 5.16269 8.44906 5.20404 8.40806L10.3138 3.29681Z"
                            fill="white"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M0.75 10.125C0.75 10.4234 0.868526 10.7095 1.0795 10.9205C1.29048 11.1315 1.57663 11.25 1.875 11.25H10.125C10.4234 11.25 10.7095 11.1315 10.9205 10.9205C11.1315 10.7095 11.25 10.4234 11.25 10.125V5.625C11.25 5.52554 11.2105 5.43016 11.1402 5.35984C11.0698 5.28951 10.9745 5.25 10.875 5.25C10.7755 5.25 10.6802 5.28951 10.6098 5.35984C10.5395 5.43016 10.5 5.52554 10.5 5.625V10.125C10.5 10.2245 10.4605 10.3198 10.3902 10.3902C10.3198 10.4605 10.2245 10.5 10.125 10.5H1.875C1.77554 10.5 1.68016 10.4605 1.60984 10.3902C1.53951 10.3198 1.5 10.2245 1.5 10.125V1.875C1.5 1.77554 1.53951 1.68016 1.60984 1.60984C1.68016 1.53951 1.77554 1.5 1.875 1.5H6.75C6.84946 1.5 6.94484 1.46049 7.01517 1.39017C7.08549 1.31984 7.125 1.22446 7.125 1.125C7.125 1.02554 7.08549 0.930161 7.01517 0.859835C6.94484 0.789509 6.84946 0.75 6.75 0.75H1.875C1.57663 0.75 1.29048 0.868526 1.0795 1.0795C0.868526 1.29048 0.75 1.57663 0.75 1.875V10.125Z"
                            fill="white"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_15909_16836">
                            <rect width="12" height="12" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </button>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isDeleteDisabled) return;
                        handleDeletePreset(preset);
                      }}
                      disabled={isDeleteDisabled}
                      title="Delete"
                      style={{
                        backgroundColor: "#dc3545",
                        border: "none",
                        borderRadius: "4px",
                        cursor: isDeleteDisabled ? "not-allowed" : "pointer",
                        padding: "2px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "22px",
                        height: "22px",
                        opacity: isDeleteDisabled ? 0.45 : 1,
                      }}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect width="18" height="18" rx="4" fill="#DC3545" />
                        <path
                          d="M5.875 14.5C5.53125 14.5 5.23698 14.3803 4.99219 14.141C4.7474 13.9016 4.625 13.6139 4.625 13.2778V5.33333H4V4.11111H7.125V3.5H10.875V4.11111H14V5.33333H13.375V13.2778C13.375 13.6139 13.2526 13.9016 13.0078 14.141C12.763 14.3803 12.4688 14.5 12.125 14.5H5.875ZM7.125 12.0556H8.375V6.55556H7.125V12.0556ZM9.625 12.0556H10.875V6.55556H9.625V12.0556Z"
                          fill="white"
                        />
                      </svg>
                    </button>
                  </div>
                  </div>

                  {editingImagePreset &&
                    editingImagePreset.id === preset.id &&
                    preset.presetCategory === "image" && (
                      <div
                        data-block-leave-canvas
                        style={{
                          marginTop: "8px",
                          marginBottom: "12px",
                          padding: "12px",
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                          maxWidth: "200px",
                          marginLeft: "auto",
                          marginRight: "auto",
                          width: "100%",
                          backgroundColor: "#fafafa",
                        }}
                      >
                        <h4>Edit Image Preset</h4>
                        <input
                          type="text"
                          value={editingImagePreset.name}
                          onChange={(e) =>
                            setEditingImagePreset({
                              ...editingImagePreset,
                              name: e.target.value,
                            })
                          }
                          placeholder="Preset name"
                          style={{
                            width: "100%",
                            padding: "6px",
                            border: "1px solid #ccc",
                            marginBottom: "12px",
                          }}
                        />

                        <button
                          onClick={() => {
                            const trimmedName = editingImagePreset.name.trim();

                            const isDuplicate = imagePresets.some(
                              (p) =>
                                p.id !== editingImagePreset.id &&
                                p.name.trim().toLowerCase() ===
                                  trimmedName.toLowerCase()
                            );

                            if (isDuplicate) {
                              alert(
                                "A preset with this name already exists. Please choose a different name."
                              );
                              return;
                            }

                            const updated = imagePresets.map((p) =>
                              p.id === editingImagePreset.id
                                ? { ...editingImagePreset, name: trimmedName }
                                : p
                            );

                            setImagePresets(updated);
                            saveImagePresetsToStorage(updated);
                            setEditingImagePreset(null);
                          }}
                          style={{
                            backgroundColor: "#2563eb",
                            color: "white",
                            padding: "6px 12px",
                            borderRadius: "4px",
                            border: "none",
                            cursor: "pointer",
                            marginRight: "8px",
                          }}
                        >
                          Save Changes
                        </button>

                        <button
                          onClick={() => setEditingImagePreset(null)}
                          style={{
                            backgroundColor: "#e5e7eb",
                            color: "#111827",
                            padding: "6px 12px",
                            borderRadius: "4px",
                            border: "none",
                            cursor: "pointer",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    )}

                  {editingGenericPreset &&
                    editingGenericPreset.id === preset.id &&
                    editingGenericPreset.presetCategory ===
                      preset.presetCategory && (
                      <div
                        data-block-leave-canvas
                        style={{
                          marginTop: "8px",
                          marginBottom: "12px",
                          padding: "12px",
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                          maxWidth: "200px",
                          marginLeft: "auto",
                          marginRight: "auto",
                          width: "100%",
                          backgroundColor: "#fafafa",
                        }}
                      >
                        <h4>Edit Preset</h4>
                        <input
                          type="text"
                          value={editingGenericPreset.name}
                          onChange={(e) =>
                            setEditingGenericPreset({
                              ...editingGenericPreset,
                              name: e.target.value,
                            })
                          }
                          placeholder="Preset name"
                          style={{
                            width: "100%",
                            padding: "6px",
                            border: "1px solid #ccc",
                            marginBottom: "12px",
                          }}
                        />

                        <button
                          onClick={handleSaveGenericPresetName}
                          style={{
                            backgroundColor: "#2563eb",
                            color: "white",
                            padding: "6px 12px",
                            borderRadius: "4px",
                            border: "none",
                            cursor: "pointer",
                            marginRight: "8px",
                          }}
                        >
                          Save Changes
                        </button>

                        <button
                          onClick={() => setEditingGenericPreset(null)}
                          style={{
                            backgroundColor: "#e5e7eb",
                            color: "#111827",
                            padding: "6px 12px",
                            borderRadius: "4px",
                            border: "none",
                            cursor: "pointer",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

CanvasHeightEditor.propTypes = {
  canvasHeight: PropTypes.string.isRequired,
  setCanvasHeight: PropTypes.func.isRequired,
  applyCanvasHeight: PropTypes.func,
  onAutoSizeCanvas: PropTypes.func,
  setImages: PropTypes.func.isRequired,
  images: PropTypes.array.isRequired,
  setRectangles: PropTypes.func,
  rectangles: PropTypes.array,
  setCircles: PropTypes.func,
  circles: PropTypes.array,
  setLines: PropTypes.func,
  lines: PropTypes.array,
  addImage: PropTypes.func.isRequired,
  setCanvasResizeTrigger: PropTypes.func,
  setSelectedObject: PropTypes.func,
  setSelectedImage: PropTypes.func,
  setSelectedObjects: PropTypes.func,
  setSelectedText: PropTypes.func,
  setSelectedTextId: PropTypes.func,
  setSelectedArrow: PropTypes.func,
  setSelectedLine: PropTypes.func,
  setSelectedDoubleSidedArrow: PropTypes.func,
  setAction: PropTypes.func,
  ACTIONS: PropTypes.object,
  setLockedAction: PropTypes.func,
  setShowTextBox: PropTypes.func,
  setPendingImageData: PropTypes.func,
  transformerRef: PropTypes.object,
  saveToHistory: PropTypes.func,
  editorWidth: PropTypes.number,
  /** When set, input min / sync / arrows match MyCanvas content-based minimum (e.g. values below ~30%). */
  getMinCanvasHeightPercent: PropTypes.func,
};

export default CanvasHeightEditor;
